(ns machtfit.utils
  (:require [clojure.string :as str]
            [com.rpl.specter :as sp]
            [reagent.core :as r]))

(defn build-questionpro-url [path params]
  (let [query-str (->> params
                       (remove (comp nil? val))
                       (map (fn [[k v]]
                              (str (js/encodeURIComponent (name k))
                                   "="
                                   (js/encodeURIComponent v))))
                       (str/join "&"))]
    (cond-> "https://machtfit.questionpro.eu/"
      (seq path) (str path)
      (seq query-str) (str "?" query-str))))

(defn ceil-to-decimal [decimal float_]
  (let [power (Math/pow 10 decimal)]
    (/ (Math/ceil (* float_ power)) power)))

(def bytes-in-megabyte (Math/pow 1024 2))

(defn file-size [file]
  (-> file :content .-size))

(defn files-size [files]
  (reduce (fn [acc file] (+ acc (file-size file))) 0 files))

(defn files-size-human [size]
  (-> size (/ bytes-in-megabyte) Math/ceil))

;; from http://dev.clojure.org/jira/browse/CLJ-1468

(defn deep-merge
  "Like merge, but merges maps recursively."
  {:added "1.7"}
  [& maps]
  (if (every? map? maps)
    (apply merge-with deep-merge maps)
    (last maps)))

(defn string-or-default [s default]
  (if (str/blank? s) default s))

(defn parse-json-response [response]
  (if (nil? (aget response "result" "length"))
    (.-result response)
    (js/JSON.parse (aget response "result" 0 "body" "textContent"))))

(defn- scroll-to [position]
  ;; The html, body selector is necessary for cross-browser support
  ;; See also https://machtfit.atlassian.net/browse/MAC-2141
  (-> (js/jQuery "html, body")
      (.animate
       (clj->js {:scrollTop position})
       1000)))

(defn scroll-element-into-view
  "Scroll the page so the element with `element-id` enters the viewport, via the
  browser's native `Element.scrollIntoView`. Note: this walks *every* scrollable
  ancestor including the document, so it can move the page itself — prefer
  `scroll-into-view!` when you only want to scroll a specific container without
  disturbing the page."
  ([element]
   (scroll-element-into-view element {:behavior "smooth"}))
  ([element-id options]
   (.scrollIntoView (js/document.getElementById element-id)
                    (clj->js options))))

(defn- scrollable-ancestor
  "Return the nearest ancestor of `el` whose computed `overflow-y` is `auto` or
  `scroll` and that has actual scrollable overflow (`scrollHeight > clientHeight`).
  Returns nil if no such ancestor exists."
  [el]
  (loop [node (.-parentElement el)]
    (when node
      (let [overflow-y (.-overflowY (js/getComputedStyle node))
            scrolls? (and (or (= overflow-y "auto") (= overflow-y "scroll"))
                          (> (.-scrollHeight node) (.-clientHeight node)))]
        (if scrolls?
          node
          (recur (.-parentElement node)))))))

(defn- reveal-edge
  "y-coordinate (px from the viewport top) that should be aligned with the
  container's bottom to fully reveal `el`. For an element with a following
  sibling, this is the sibling's top — so the natural inter-element gap is left
  as breathing room. For the last child, fall back to `el`'s own bottom."
  [el]
  (if-let [next-sibling (.-nextElementSibling el)]
    (.-top (.getBoundingClientRect next-sibling))
    (.-bottom (.getBoundingClientRect el))))

(defn- reveal-delta
  "Vertical scroll offset (px, positive = scroll down) to apply to `container`
  so `el` becomes fully visible with natural breathing room. Strategy:

  - If `el` is taller than the container, align its top edge to the container's
    top so the start of its content stays visible (bottom inevitably clips).
  - Otherwise, when `el`'s bottom is below the fold, scroll just enough to bring
    its `reveal-edge` to the container's bottom — so `el` shows fully and the
    inter-element gap (or, for the last child, its own bottom) is the breathing
    room.
  - If `el`'s top is above the container's top, scroll up by just that amount.
  - Otherwise return 0 (already fully visible)."
  [container el]
  (let [container-rect (.getBoundingClientRect container)
        el-rect (.getBoundingClientRect el)
        el-top-below-viewport-top (- (.-top el-rect) (.-top container-rect))
        el-bottom-below-viewport-bottom (- (.-bottom el-rect) (.-bottom container-rect))
        reveal-edge-below-viewport-bottom (- (reveal-edge el) (.-bottom container-rect))
        taller-than-viewport? (> (.-height el-rect) (.-height container-rect))]
    (cond
      taller-than-viewport? el-top-below-viewport-top
      (pos? el-bottom-below-viewport-bottom) reveal-edge-below-viewport-bottom
      (neg? el-top-below-viewport-top) el-top-below-viewport-top
      :else 0)))

(defn scroll-into-view!
  "After the next reagent render, smoothly scroll the nearest scrollable
  container so `el` becomes fully visible with breathing room below. Scrolls
  *only* that container — the document is never scrolled, so the surrounding
  page does not jump.

  Pair this with a synchronous state update (e.g. `rf/dispatch-sync`) before
  calling, so the post-update layout is in the DOM by the time the
  `after-render` callback measures it. With async `rf/dispatch` the
  measurement is racy — the expansion may not yet be rendered."
  [el]
  (r/after-render
   #(when-let [container (scrollable-ancestor el)]
      (let [delta (reveal-delta container el)]
        (when-not (zero? delta)
          (.scrollBy container #js {:top delta :behavior "smooth"}))))))

(defn scroll-down
  "Scroll down to the bottom of the page to move into the viewport a section that was revealed at
  the bottom of the page in response to a button click."
  []
  (scroll-to (aget js/window "document" "body" "scrollHeight")))

(defn scroll-up []
  (scroll-to 0))

(defn save-scroll-position [current-db]
  (assoc-in current-db [:ui :scroll-height] (aget js/window "document" "body" "scrollHeight")))

(defn- contains-word [string word]
  (> (.indexOf (str/lower-case string) (str/lower-case word)) -1))

(defn matches-filter [string filter]
  (every? true? (map #(contains-word string %) (str/split filter #"(?i)[^a-z0-9äöüß]+"))))

(defn in?
  "true if coll contains element"
  [coll element]
  (boolean (some #(= element %) coll)))

(def not-in?
  "true if coll does not contain element"
  (complement in?))

(defn compare-by
  "Generates a Comparator which can be passed to sort. It allows to specify mutliple sort fields
  and the desired sort order like this:
  (sort (compare-by :last-name :asc, :date-of-birth :desc) coll)"
  [& key-order-pairs]
  (fn [x y]
    (loop [[k order & more] key-order-pairs]
      (let [cmp (case order :asc compare :desc #(compare %2 %1))
            result (cmp (k x) (k y))]
        (if (and (zero? result) more)
          (recur more)
          result)))))

;; Some vector helpers, useful for our rule logics

(defn move-down [v i]
  ;; silently return unmodified vector in case of invalid index
  (cond->> v
    (<= 0 i (-> v count dec dec)) (sp/setval (sp/index-nav i) (inc i))))

(defn move-up [v i]
  (move-down v (dec i)))

(defn delete-at [v i]
  ;; silently return unmodified vector in case of invalid index
  (cond->> v
    (<= 0 i (-> v count dec)) (sp/setval i sp/NONE)))

(defn decrease-index [i]
  (and i (dec i)))

(defn increase-index [i]
  (and i (inc i)))

(defn by-id [m id]
  (first (filter #(= id (:id %)) m)))

(defn toggle-in-set [coll val]
  (let [conj-or-disj (if (contains? coll val) disj conj)]
    (conj-or-disj coll val)))

(defn- rank
  "Assign a rank to items. If two consecutive items are equal by rank-key,
  assign the same rank to both and skip the next rank.

  The item order is not changed. It's usually sensible to sort items before ranking them.

  Returns a transducer when no collection is provided.

  Sample usage:

  my.ns> (rank identity [:a :c :c :b])
  ([1 :a] [2 :c] [2 :c] [4 :b])

  my.ns> (rank :points [{:name :a :points 400}
                        {:name :b :points 300}
                        {:name :c :points 300}
                        {:name :d :points 150}])
  ([1 {:name :a :points 400}]
   [2 {:name :b :points 300}]
   [2 {:name :c :points 300}]
   [4 {:name :d :points 150}])

  my.ns> (into [] (comp (rank :points)
                        (map #(assoc (second %) :rank (first %))))
                  [{:name :a :points 400}
                   {:name :b :points 300}
                   {:name :c :points 300}
                   {:name :d :points 150}])
  [{:name :a :points 400 :rank 1}
   {:name :b :points 300 :rank 2}
   {:name :c :points 300 :rank 2}
   {:name :d :points 150 :rank 4}]
  "
  ([rank-key]
   (fn [rf]
     (let [prev (volatile! ::none)
           index (volatile! 0)
           rank' (volatile! 0)]
       (completing
        (fn
          [result input]
          (let [prev-value @prev
                this-value (rank-key input)]

            (vreset! prev this-value)

            (vswap! index inc)

            (when-not (= prev-value this-value)
              (vreset! rank' @index))

            (rf result [@rank' input])))))))

  ([rank-key coll]
   (sequence (rank rank-key) coll)))

(defn map-ranked
  "Returns a lazy sequence of the result of applying f to the rank of the item and the item itself
  for all items it coll. Thus function f should accept 2 arguments, rank and item.

  Returns a stateful transducer when no collection is provided."
  ([rank-key f]
   (comp (rank rank-key)
         (map #(apply f %))))
  ([rank-key f coll]
   (sequence (map-ranked rank-key f) coll)))
